import cloneDeep from 'lodash.clonedeep';
import httpStatusCodes from "http-status-codes";
import objectHandle from './baseLib/objectHandle';
import { throwError } from './declareErrorCode';

/** 定义通用基础类型约束 */
export declare type universalBasicType<TId extends string | number> = {
  /** id */
  id: TId;
  /** 名称 */
  name?: string;
  /** 数据 */
  data?: any;
  /** 其它参数 */
  [key: string]: any;
};

/** 联合转交叉，并保证结果满足 modes 约束 */
type UnionToIntersection<U> =
  (U extends any ? (k: U) => void : never) extends (k: infer I) => void
  ? I extends Record<string | number, universalBasicType<string | number>>
  ? I
  : never
  : never;

/** 从工厂实例中提取 modes 类型 */
type FactoryModes<F> =
  F extends universalBasicTypeFactory<infer M> ? M : never;

/**
 * getIds 可选参数类型
 * 
 * 全部可选，不传则返回原始 id 数组。
 * 
 * @example
 * // 加前缀
 * Charsets.getIds({ prefix: 'pre_' });
 * // 加后缀
 * Charsets.getIds({ suffix: '_suf' });
 * // 同时加前后缀
 * Charsets.getIds({ prefix: 'pre_', suffix: '_suf' });
 */
type GetIdsOptions = {
  /** 前缀 */
  prefix?: string;
  /** 后缀 */
  suffix?: string;
};

/**
 * 根据 options 推断 getIds 返回值类型
 * 
 * - 不传 prefix / suffix 时，返回原始 id 类型数组。
 * - 传了 prefix 或 suffix 时，返回 string[]（拼接后一定是字符串）。
 */
type GetIdsResult<
  T extends Record<string | number, universalBasicType<string | number>>,
  Options extends GetIdsOptions
> = Options extends { prefix: any } | { suffix: any }
  ? string[]
  : (T[keyof T]['id'])[];

/**
 * 重构工厂类型（支持任意符合该约束的类型）
 * 
 * 该类型包含了 getAll、getIds、getIdsForKey 三个方法。
 * 所有新增参数（前后缀等）都是可选的，旧调用不受影响。
 */
export declare type universalBasicTypeFactory<T extends Record<string | number, universalBasicType<string | number>>> = {
  [K in keyof T]: T[K];
} & {
  /**
   * 获取所有（支持可选过滤、字段选择、前后缀）
   * 
   * 参数 options 全部可选：
   * - filter    过滤条件，保留匹配该属性的成员。例如 { id: 'BASE10' }
   * - select    需要保留的字段名数组。例如 ['id', 'name']
   * - prefix    统一前缀，默认作用于 affixKeys 中的字段（默认 ['id']）
   * - suffix    统一后缀，默认作用于 affixKeys 中的字段（默认 ['id']）
   * - affixKeys 对哪些字段加前后缀，默认 ['id']，可传 ['id', 'name']
   * - affix     按字段单独配置前后缀，优先级高于统一 prefix/suffix。
   *             例如 { id: { prefix: 'id_', suffix: '_end' }, name: { prefix: '名称：' } }
   * 
   * 处理顺序：先 filter → 再加前后缀 → 最后 select。
   * 
   * @example
   * // 1. 无参：返回所有成员的完整对象数组
   * Charsets.getAll();
   * 
   * // 2. 只看 id 和 name 字段
   * Charsets.getAll({ select: ['id', 'name'] });
   * 
   * // 3. 过滤出 id 为 BASE10 的成员
   * Charsets.getAll({ filter: { id: 'BASE10' } });
   * 
   * // 4. 过滤 + 选择字段
   * Charsets.getAll({ filter: { id: 'BASE10' }, select: ['id', 'name'] });
   * 
   * // 5. 给 id 统一加前缀（默认 affixKeys=['id']，只对 id 生效）
   * Charsets.getAll({ prefix: 'pre_' });
   * 
   * // 6. 给 id 统一加后缀
   * Charsets.getAll({ suffix: '_suf' });
   * 
   * // 7. 给 id 同时加前后缀
   * Charsets.getAll({ prefix: 'pre_', suffix: '_suf' });
   * 
   * // 8. 给 id 和 name 同时加统一前缀
   * Charsets.getAll({ prefix: 'pre_', affixKeys: ['id', 'name'] });
   * 
   * // 9. 给 id 和 name 分别加不同的前后缀
   * Charsets.getAll({
   *   affix: {
   *     id:   { prefix: 'id_',   suffix: '_end'  },
   *     name: { prefix: '名称：' },
   *   },
   * });
   * 
   * // 10. 过滤 + 前后缀 + 字段选择 组合
   * Charsets.getAll({
   *   filter: { id: 'BASE10' },
   *   prefix: 'pre_',
   *   select: ['id', 'name'],
   * });
   */
  getAll: {
    /** 无参：返回所有成员 */
    (): T[keyof T][];
    /** 传入 options：根据 filter/select/前后缀 返回处理后的数组 */
    <Options extends GetAllOptions<T>>(options: Options): GetAllResult<T, Options>;
  };

  /**
   * 获取所有 id 数组（支持可选前后缀）
   * 
   * 参数 options 全部可选：
   * - prefix 前缀
   * - suffix 后缀
   * 
   * 注意：getIds 只返回 id，所以它只能对 id 加前后缀，不能对 name 加。
   * 
   * @example
   * // 1. 无参：返回原始 id 数组
   * Charsets.getIds();
   * 
   * // 2. 加前缀
   * Charsets.getIds({ prefix: 'pre_' });
   * 
   * // 3. 加后缀
   * Charsets.getIds({ suffix: '_suf' });
   * 
   * // 4. 同时加前后缀
   * Charsets.getIds({ prefix: 'pre_', suffix: '_suf' });
   * 
   * // 5. 数字型 id 也能拼接（如 CalcModes 的 id 是 number）
   * CalcModes.getIds({ prefix: 'C' }); // 返回 ['C1', 'C2']
   */
  getIds: {
    /** 无参：返回原始 id 数组 */
    (): (T[keyof T]['id'])[];
    /** 传入 options：根据 prefix/suffix 返回处理后的 id 数组 */
    <Options extends GetIdsOptions>(options: Options): GetIdsResult<T, Options>;
  };

  /** 根据 keyId 获取集合 */
  getIdsForKey: <K extends string>(keyId: K) =>
    Extract<T[keyof T], { [P in K]: unknown }>[K][];
};

/**
 * getAll 可选参数类型
 * 
 * 所有字段都是可选的，不传则行为与以前完全一致。
 */
type GetAllOptions<T extends Record<string | number, universalBasicType<string | number>>> = {
  /** 过滤条件，保留匹配该属性的成员。例如 { id: 'BASE10' } */
  filter?: Partial<T[keyof T]>;
  /** 需要保留的字段名数组。例如 ['id', 'name'] */
  select?: (keyof T[keyof T])[];
  /** 统一前缀，默认作用于 affixKeys 中的字段（默认 ['id']） */
  prefix?: string;
  /** 统一后缀，默认作用于 affixKeys 中的字段（默认 ['id']） */
  suffix?: string;
  /** 对哪些字段加前后缀，默认 ['id']，可传 ['id', 'name'] */
  affixKeys?: (keyof T[keyof T])[];
  /** 按字段单独配置前后缀，优先级高于统一 prefix/suffix */
  affix?: Partial<{
    [K in keyof T[keyof T]]: {
      prefix?: string;
      suffix?: string;
    };
  }>;
};

/**
 * 根据 options 推断 getAll 返回值类型
 * 
 * - 不传前后缀相关参数时，按 select 精确推导：
 *   无 select → T[keyof T][]
 *   有 select → Pick<T[keyof T], S>[]
 * - 传了前后缀相关参数时，返回类型放宽为 any[]（因为 id 可能被转成字符串，
 *   TS 难以精确推导），调用处可按需断言或直接使用。
 */
type GetAllResult<
  T extends Record<string | number, universalBasicType<string | number>>,
  Options extends GetAllOptions<T>
> = Options extends
  | { prefix: any }
  | { suffix: any }
  | { affix: any }
  | { affixKeys: any }
  ? any[]
  : Options extends { select: infer S extends (keyof T[keyof T])[] }
  ? Pick<T[keyof T], S[number]>[]
  : T[keyof T][];

/**
 * 通用工厂函数实现（支持自动类型推导）
 * @param modes 
 * @returns 
 */
export const createTypeFactory = <T extends Record<string | number, universalBasicType<string | number>>>(
  modes: T
): universalBasicTypeFactory<T> => ({
  ...modes,
  /**
   * 获取所有（支持可选过滤、字段选择、前后缀）
   * 
   * 处理顺序：先 filter → 再加前后缀 → 最后 select。
   * 所有新增参数都是可选的，不传时行为与旧版完全一致。
   */
  getAll: ((options?: GetAllOptions<T>) => {
    const values = Object.values(modes) as T[keyof T][];
    if (!options) return values;

    let result: any[] = values;

    // 过滤：保留匹配 filter 中所有键值对的成员
    if (options.filter) {
      result = result.filter(item =>
        Object.entries(options.filter!).every(([key, val]) => item[key] === val)
      );
    }

    // 加前缀/后缀：仅当传了 prefix、suffix、affix 或 affixKeys 时才执行
    const hasAffix =
      options.prefix !== undefined ||
      options.suffix !== undefined ||
      options.affix !== undefined ||
      (options.affixKeys && options.affixKeys.length > 0);

    if (hasAffix) {
      // 确定要加前后缀的字段列表：
      // 优先用 affixKeys，其次用 affix 的键，最后默认为 ['id']
      const affixKeys: (keyof T[keyof T])[] =
        options.affixKeys ??
        (options.affix
          ? (Object.keys(options.affix) as (keyof T[keyof T])[])
          : (['id'] as (keyof T[keyof T])[]));

      result = result.map(item => {
        const newItem: any = { ...item };

        for (const key of affixKeys) {
          const val = newItem[key];

          if (val === undefined || val === null) continue;
          if (typeof val !== 'string' && typeof val !== 'number') continue;

          // 按字段单独配置的前后缀优先，其次用统一 prefix/suffix
          const fieldAffix = options.affix?.[key] as
            | { prefix?: string; suffix?: string }
            | undefined;

          const prefix = fieldAffix?.prefix ?? options.prefix ?? '';
          const suffix = fieldAffix?.suffix ?? options.suffix ?? '';

          newItem[key] = `${prefix}${val}${suffix}`;
        }

        return newItem;
      });
    }

    // 字段选择：只保留 select 中指定的字段
    if (options.select && options.select.length > 0) {
      result = result.map(item => {
        const picked: any = {};
        options.select!.forEach(k => {
          if (k in item) picked[k] = item[k];
        });
        return picked;
      });
    }

    return result;
  }) as universalBasicTypeFactory<T>['getAll'],

  /**
   * 获取 ids（支持可选前后缀）
   * 
   * 不传 options 或没传 prefix/suffix 时，直接返回原始 id 数组。
   * 传了 prefix 或 suffix 时，返回拼接后的 string[]。
   */
  getIds: ((options?: GetIdsOptions) => {
    const ids = Object.values(modes).map(m => m.id);

    // 没传或没传前后缀，直接返回原数组
    if (
      !options ||
      (options.prefix === undefined && options.suffix === undefined)
    ) {
      return ids;
    }

    const prefix = options.prefix ?? '';
    const suffix = options.suffix ?? '';

    return ids.map(id => `${prefix}${id}${suffix}`);
  }) as universalBasicTypeFactory<T>['getIds'],

  /** 根据 keyId 获取集合 */
  getIdsForKey: <K extends string>(keyId: K) =>
    Object.values(modes)
      .filter((m): m is Extract<T[keyof T], { [P in K]: unknown }> => keyId in m)
      .map(m => m[keyId])
});

/**
 * 扩展工厂函数（从已有的工厂函数实例继承）
 * @param baseFactory 基础工厂实例
 * @param additionalModes 要添加的新模式
 * @returns 新的工厂函数实例
 */
export const extendTypeFactory = <
  T extends Record<string | number, universalBasicType<string | number>>,
  U extends Record<string | number, universalBasicType<string | number>>
>(
  baseFactory: universalBasicTypeFactory<T>,
  additionalModes: U
): universalBasicTypeFactory<T & U> => {
  // 从基础工厂实例中提取原始配置
  const baseModes: T = {} as T;
  // 遍历 baseFactory 的属性，排除方法
  Object.keys(baseFactory).forEach(key => {
    if (
      key !== 'getAll' &&
      key !== 'getIds' &&
      key !== 'getIdsForKey' &&
      typeof (baseFactory as any)[key] === 'object'
    ) {
      (baseModes as any)[key] = (baseFactory as any)[key];
    }
  });
  // 合并配置
  const mergedModes = {
    ...baseModes,
    ...additionalModes
  } as T & U;
  // 使用合并后的配置创建新实例
  return createTypeFactory(mergedModes);
};

/**
 * 从工厂实例 TFactory 中提取成员类型，支持：
 * - 按条件过滤成员（filter）
 * - 指定哪些属性必选（select），其余属性可选
 * 
 * @template TFactory - 工厂实例类型，必须满足 universalBasicTypeFactory 约束
 * @template Options - 配置对象，包含可选的 filter 和 select
 *                     默认值为 {}，表示不过滤且只将 'id' 设为必选
 * 
 * @example
 * // 1. 默认用法：id 必选，其他可选
 * type Mode = createDeclareType<typeof ShowModes>;
 * 
 * // 2. 过滤 + 指定必选键
 * type Selectable = createDeclareType<
 *   typeof ShowModes,
 *   { filter: { isSelect: true }; select: 'id' | 'name' }
 * >;
 */
export type createDeclareType<
  TFactory extends universalBasicTypeFactory<any>,
  Options extends {
    /** 过滤条件：仅保留满足该条件的成员，如 { isSelect: true } */
    filter?: Partial<ReturnType<TFactory["getAll"]>[number]>;
    /** 必选属性键：指定的属性将在生成类型中为必选，其余属性为可选 */
    select?: keyof ReturnType<TFactory["getAll"]>[number];
  } = {} // 默认空对象 → 不过滤且 select 默认为 'id'
> = Pick<
  // ===== 第一部分：必选属性 =====
  Extract<
    // 从工厂 getAl() 返回的联合类型中取出所有成员
    ReturnType<TFactory["getAll"]>[number],

    // 解析 Options 中的 filter，若未提供则使用 {}（匹配所有成员）
    Options extends { filter: infer F }
    ? F extends Record<string, any>
    ? F
    : {}
    : {}
  >,

  // 解析 Options 中的 select，若未提供则使用 'id'
  Options extends { select: infer S }
  ? S extends keyof ReturnType<TFactory["getAll"]>[number]
  ? S
  : 'id'
  : 'id'
> &
  // ===== 第二部分：可选属性 =====
  Partial<
    Omit<
      // 同样按 filter 过滤后的成员类型
      Extract<
        ReturnType<TFactory["getAll"]>[number],
        Options extends { filter: infer F }
        ? F extends Record<string, any>
        ? F
        : {}
        : {}
      >,
      // 排除已设为必选的键，剩余属性变为可选
      Options extends { select: infer S }
      ? S extends keyof ReturnType<TFactory["getAll"]>[number]
      ? S
      : 'id'
      : 'id'
    >
  >;

/**
 * 通过id值查找类型内容
 * @param arr 数组 
 * @param id id值
 * @param options 选项 
 * @returns 
 */
export const findDeclareTypeById = (
  arr: universalBasicType<string | number>[],
  id: number | string,
  options?: {
    /** 删除item */
    removeItem?: Array<string>,
    /** id名称 */
    idName?: string
  }) => {
  let item;
  try {
    const { removeItem = [], idName = 'id' } = options || {};
    const foundItem = Object.values(arr).find((item) => item[idName] === id);
    if (foundItem) {
      // item = structuredClone(foundItem);
      item = cloneDeep(foundItem);
      for (const value of removeItem) {
        delete item[value];
      }
    }
  }
  catch { }
  return item;
};

/**
 * 数组创建类型安全工厂实例
 * @param arr 数组
 * @returns 
 */
export const createTypeFactoryFromArray = <
  T extends ReadonlyArray<universalBasicType<string | number>>
>(
  arr: T
) => {
  const map = Object.fromEntries(arr.map((item) => [item.id, item])) as {
    [K in T[number]as K['id']]: K;
  };
  return createTypeFactory(map);
};

/**
 * 合并多个类型工厂为一个新工厂
 * - 支持 N 个工厂，传多少合并多少
 * - 只合并成员数据，过滤掉 getAll/getIds/getIdsForKey
 * - key 冲突时后面的覆盖前面的
 */
export const mergeTypeFactory = <
  F extends universalBasicTypeFactory<any>[]
>(
  ...factories: F
): universalBasicTypeFactory<UnionToIntersection<FactoryModes<F[number]>>> => {
  const mergedModes: Record<string, any> = {};

  for (const factory of factories) {
    for (const key of Object.keys(factory)) {
      if (key === 'getAll' || key === 'getIds' || key === 'getIdsForKey') continue;
      const v = (factory as any)[key];
      if (v && typeof v === 'object') mergedModes[key] = v;
    }
  }

  // 运行时数据正确，类型层面 TS 证不出来，用 as 断言兜底
  return createTypeFactory(mergedModes as any) as universalBasicTypeFactory<
    UnionToIntersection<FactoryModes<F[number]>>
  >;
};

/** 公共属性 */
export interface CommonProps {
  /** 业务标识 */
  apiId: string;
  /** 服务基础地址 */
  defaultService: string;
  /** 业务列 */
  columns: Array<any>;
  /** 实体名称 */
  titleName: string;
  /** 主键，默认 'id' */
  primaryKeyName?: string;
  /**
   * 关闭窗口
   * @param result 返回结果
   * @returns 
   */
  closeModal?: (result?: RtnType<any>) => void;
  /** 显示 */
  visible?: boolean;
};

/** showPage属性 */
export interface ShowPageProps {
  /** 数据列 */
  rows?: Array<any>;
  /** 总行数 */
  total?: number;
  /** 当前页，默认：1 */
  current?: number;
  /** 每页行数，默认：10 */
  pageSize?: number;
  /** 查询条件 */
  queryString?: string;
  /** 排序，返回数据格式，例如：giftCardStatus@ASC */
  orderByString?: string;
  /** 使用备份 */
  useBak?: boolean;
  /** 传递的页面数据 */
  sendPageData?: any;
};

/** 微信小程序版本对象 */
export const WxMiniAppEnvVersions = createTypeFactory({
  /** 开发版 */
  develop: { id: 'develop', name: '开发版' },
  /** 体验版 */
  trial: { id: 'trial', name: '体验版' },
  /** 正式版 */
  release: { id: 'release', name: '正式版' },
} as const);

/** 微信小程序版本类型 */
export declare type WxMiniAppEnvVersionType = createDeclareType<typeof WxMiniAppEnvVersions>;

/** 微信小程序版本string类型 */
export type WxMiniAppEnvVersionStringType = ReturnType<typeof WxMiniAppEnvVersions.getIds>[number];

/** 字符集对象 */
export const Charsets = createTypeFactory({
  /** BASE10 */
  BASE10: { id: 'BASE10', name: '数字', data: '0123456789' },
  /** BASE26 */
  BASE26: { id: 'BASE26', name: '小字母', data: 'abcdefghijklmnopqrstuvwxyz' },
  /** BASEUPPER26 */
  BASEUPPER26: { id: 'BASEUPPER26', name: '大字母', data: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ' },
  /** BASE36 */
  BASE36: { id: 'BASE36', name: '小字母+数字', data: 'abcdefghijklmnopqrstuvwxyz0123456789' },
  /** BASEUPPER36 */
  BASEUPPER36: { id: 'BASEUPPER36', name: '大字母+数字', data: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789' },
  /** BASE52 */
  BASE52: { id: 'BASE52', name: '大小字母', data: 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz' },
  /** BASE62 */
  BASE62: { id: 'BASE62', name: '大小字母+数字', data: 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789' }
} as const);

/** 字符集类型 */
export declare type CharsetType = createDeclareType<typeof Charsets>;

/** 字符集string类型 */
export type CharsetStringType = ReturnType<typeof Charsets.getIds>[number];

/** 请求对象 */
export const Methods = createTypeFactory({
  /** GET */
  GET: { id: 'GET', name: 'GET查询', details: '从服务器查询，可以在服务器通过请求的参数区分查询的方式' },
  /** POST */
  POST: { id: 'POST', name: 'POST新建', details: '在服务器新建一个资源，调用insert操作' },
  /** PUT */
  PUT: { id: 'PUT', name: 'PUT更新', details: '在服务器更新资源，调用update操作' },
  /** DELETE */
  DELETE: { id: 'DELETE', name: 'DELETE删除', details: '从服务器删除资源，调用delete语句' },
  /** HEAD */
  HEAD: { id: 'HEAD', name: 'HEAD', details: '请求一个与GET请求的响应相同的响应，但没有响应体' },
  /** CONNECT */
  CONNECT: { id: 'CONNECT', name: 'CONNECT', details: '建立一个到由目标资源标识的服务器的隧道' },
  /** OPTIONS */
  OPTIONS: { id: 'OPTIONS', name: 'OPTIONS', details: '用于描述目标资源的通信选项' },
  /** TRACE */
  TRACE: { id: 'TRACE', name: 'TRACE', details: '沿着到目标资源的路径执行一个消息环回测试' },
  /** PATCH */
  PATCH: { id: 'PATCH', name: 'PATCH', details: '用于对资源应用部分修改' }
} as const);

/**
 * 请求的类型
 * @type GET（SELECT）：从服务器查询，可以在服务器通过请求的参数区分查询的方式。
 * @type POST（CREATE）：在服务器新建一个资源，调用insert操作。
 * @type PUT（UPDATE）：在服务器更新资源，调用update操作。
 * @type DELETE（REMOVE）：从服务器删除资源，调用delete语句
 * @type HEAD：请求一个与GET请求的响应相同的响应，但没有响应体.
 * @type CONNECT ：建立一个到由目标资源标识的服务器的隧道。
 * @type OPTIONS ： 用于描述目标资源的通信选项。
 * @type TRACE ： 沿着到目标资源的路径执行一个消息环回测试。
 * @type PATCH ： 用于对资源应用部分修改。
 */
export declare type MethodType = ReturnType<typeof Methods.getIds>[number];

/** 通信模式对象 */
export const ProtocolModes = createTypeFactory({
  /** MQTT消息队列遥测传输协议 */
  MQTT: { id: 'MQTT', name: 'MQTT消息队列遥测传输协议' },
  /** HTTP超文本传输协议 */
  HTTP: { id: 'HTTP', name: 'HTTP超文本传输协议' },
  /** WebSocket全双工通信协议 */
  WebSocket: { id: 'WebSocket', name: 'WebSocket全双工通信协议' },
  /** TCP传输控制协议 */
  TCP: { id: 'TCP', name: 'TCP传输控制协议' },
} as const);

/** 通信模式string类型 */
export type ProtocolModeStringType = ReturnType<typeof ProtocolModes.getIds>[number];

/** ‌互联网协议版本对象 */
export const Protocols = createTypeFactory({
  /** ipV4 */
  ipV4: { id: 'IPV4', name: '版本4（IPv4）' },
  /** ipV6 */
  ipV6: { id: 'IPV6', name: '版本6（IPv6）' },
} as const);

/** ‌互联网协议版本类型 */
export type ProtocolType = createDeclareType<typeof Protocols>;

/** 平台对象 */
export const AppPlatforms = createTypeFactory({
  /** windows */
  windows: { id: 'WINDOWS', name: '微软windows' },
  /** linux */
  linux: { id: 'LINUX', name: 'LINUX' },
  /** 安卓 */
  android: { id: 'ANDROID', name: '安卓' }
} as const); //as const 确保字面量类型推断

/** 平台类型 */
export type AppPlatformType = createDeclareType<typeof AppPlatforms>;

/** app架构对象 */
export const AppArchs = createTypeFactory({
  /** x86 */
  x86: { id: 'X86', name: '32位' },
  /** x64 */
  x64: { id: 'X64', name: '64位' }
} as const);

/** 平台类型 */
export type AppArchType = createDeclareType<typeof AppArchs>;

/** 排序方式对象 */
export const SortBys = createTypeFactory({
  /** 升序 */
  ASC: { id: 'ASC', name: '升序' },
  /** 降序 */
  DESC: { id: 'DESC', name: '降序' }
} as const);

/** 排序方式类型，asc：升序；desc：降序； */
export declare type SortByType = ReturnType<typeof SortBys.getIds>[number];

/** Uuid短码长度类型 */
export declare type IdLenghtType = 8 | 16 | 32;

/** 字母类型，UPPERCASE：大写，LOWERCASE：小写，ALL：所有 */
export declare type LetterFixType = 'UPPERCASE' | 'LOWERCASE' | 'ALL';

/** dateTime数据类型 */
export declare type DateTimeType = 'TIMESTAMP' | 'DATETIME' | 'DATE' | 'TIME' | 'YEAR' | 'MONTH' | 'DAY' | 'HOUR' | 'MINUTE' | 'SECOND';

/** snowflakeId 前后缀数据类型 */
export declare type SnowflakeIdFixType = "PREFIX" | "SUFFIX";

/** 编码类型 */
export declare type EncodingType = 'GBK' | 'UTF8';

/** 对齐类型 */
export declare type AlignType = 'LEFT' | 'RIGHT' | 'CENTER';

/** 位置类型 */
export declare type DirectionType = 'START' | 'END' | 'CENTER';

/** 设备型号对象 */
export const DeviceModels = createTypeFactory({
  /** DP320PUW热敏打印机 */
  DP320PUW: { id: 'DP320PUW', name: 'DP320PUW热敏打印机', brand: '大通优联' }
} as const);

export declare type DeviceModelType = createDeclareType<typeof DeviceModels>;

/** 数据库系统对象 */
export const DbSystems = createTypeFactory({
  /** MSSQL */
  MSSQL: { id: 'MSSQL', name: 'MSSQL' },
  /** PostgreSQL */
  PostgreSQL: { id: 'PostgreSQL', name: 'PostgreSQL' },
  /** MySQL */
  MySQL: { id: 'MySQL', name: 'MySQL' }
} as const);

/** 数据库系统string类型 */
export type DbSystemStringType = ReturnType<typeof DbSystems.getIds>[number];

/**  join对象 */
export const JoinTypes = createTypeFactory({
  /** 左联 */
  leftJoin: { id: 'leftJoin', name: '左联' },
  /** 左联and选择 */
  leftJoinAndSelect: { id: 'leftJoinAndSelect', name: '左联and选择' },
  /** 内联 */
  innerJoin: { id: 'innerJoin', name: '内联' },
  /** 内联and选择 */
  innerJoinAndSelect: { id: 'innerJoinAndSelect', name: '内联and选择' }
} as const);

/** joinString类型 */
export declare type JoinStringType = ReturnType<typeof JoinTypes.getIds>[number];

/** 文件有效期对象 */
export const FileValidityPeriods = createTypeFactory({
  /** 临时 */
  fixedTerm: { id: 'fixedTerm', name: '临时' },
  /** 永久 */
  permanent: { id: 'permanent', name: '永久' }
} as const);

/** 文件有效期string类型 */
export type FileValidityPeriodStringType = ReturnType<typeof FileValidityPeriods.getIds>[number];

/** 数据来源性质对象 */
export const DataSourceOrigins = createTypeFactory({
  /** 原始数据 */
  raw: { id: 'raw', name: '原始数据' },
  /** 备份数据 */
  backup: { id: 'backup', name: '备份数据' },
  /** 副本数据 */
  replica: { id: 'replica', name: '副本数据' },
} as const);

/** 数据来源性质string类型 */
export type DataSourceOriginStringType = ReturnType<typeof DataSourceOrigins.getIds>[number];

/** 计算模式 */
export const CalcModes = createTypeFactory({
  /** 按数值 */
  1: { id: 1, name: '按数值' },
  /** 按比例 */
  2: { id: 2, name: '按比例' },
} as const);

/** 计算模式number类型 */
export type CalcModeNumberType = ReturnType<typeof CalcModes.getIds>[number];

/** 到期规则 */
export const ExpiryRules = createTypeFactory({
  /** 当前时间+24小时 */
  0: { id: 0, name: '当前时间+24小时' },
  /** 次日零点 */
  1: { id: 1, name: '次日零点' },
  /** 当前时间+1小时 */
  2: { id: 2, name: '当前时间+1小时' },
  /** 当前时间+7天 */
  3: { id: 3, name: '当前时间+7天' },
  /** 本周日结束 */
  4: { id: 4, name: '本周日结束' },
  /** 本月最后一天结束 */
  5: { id: 5, name: '本月最后一天结束' },
  /** 本季度最后一天结束 */
  6: { id: 6, name: '本季度最后一天结束' },
  /** 本年最后一天结束 */
  7: { id: 7, name: '本年最后一天结束' },
  /** 永久有效 */
  99: { id: 99, name: '永久有效' }
} as const);

/** 到期规则number类型 */
export type ExpiryRuleNumberType = ReturnType<typeof ExpiryRules.getIds>[number];

/** ---------- RTN ---------- */
/** 返回数据类型 */
export interface RtnType<T = any> {
  /** 成功标志 */
  success: boolean,
  /** 数据 */
  data?: T,
  /** 错误代码 */
  errorCode?: number | string,
  /** 信息 */
  message?: string
};

/**
 * 默认rtn(重载函数)
 * @param options 选项 {
 * success: false,
 * data: undefined,
 * errorCode: undefined,
 * message: ''
 * }
 */
export function defaultRtn<T = any>(): RtnType<T>;
export function defaultRtn<T = any>(options: { data: T } & Partial<RtnType<T>>): Omit<RtnType<T>, 'data'> & { data: T };
export function defaultRtn<T = any>(options?: Partial<RtnType<T>>): RtnType<T>;
export function defaultRtn<T = any>(options?: Partial<RtnType<T>>): RtnType<T> {
  const { success = false, data, errorCode, message = '' } = options || {};
  return { success, data, errorCode, message };
};

/**
 * 统一的错误处理函数
 * @param isThrowError 是否抛出错误
 * @returns rtn
 * @example  const handleError = createErrorHandler(isThrowError);
 *  return handleError({rtn,
           message: `参数缺失，${requestName} 对象参数为 undefined、null 或空字符串。`,
           errorCode: 400
           });
 */
export const createErrorHandler = (isThrowError: boolean = false) => {
  return (
    /** 选项 */
    options: {
      /** Rtn */
      rtn?: RtnType,
      /** 信息 */
      message: string
      /** 错误代码 */
      errorCode?: number | string
    }) => {
    const { rtn = defaultRtn(), message, errorCode } = options;
    rtn.message = message;
    rtn.errorCode = errorCode ? errorCode : rtn.errorCode;
    if (isThrowError) {
      throwError(objectHandle.isNumber(errorCode) ? errorCode : httpStatusCodes.BAD_REQUEST, rtn.message);
    }
    return rtn;
  };
};
/** ---------- RTN ---------- */